import { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import { ActivityIndicator, Provider as ProviderPaper } from "react-native-paper";
import { Feather } from "@expo/vector-icons";
import ViewportProvider from "../../hooks/ViewPortProvider";
import Header from "../../components/Header";
import FormCustomLogin from "./FormCustomLogin";
import { Login } from "../Login";
import { useCustomLogin } from "./useCustomLogin";
import { useNavigation } from "@react-navigation/native";
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";

export const CustomLogin = () => {
  const navigation = useNavigation();
  const { isLoadingColorInfo } = useCustomLogin();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [isLoginFullWidth, setIsLoginFullWidth] = useState(false);
  const [isSave, setIsSave] = useState(false);
  const handleSave = () => {
    setIsSave(true);
    setTimeout(() => setIsSave(false), 2000);
  };

  return (
    <ViewportProvider>
      <ProviderPaper>
        <View style={{ flex: 1, backgroundColor: ADMIN_COLORS.surfaceSubtle }}>
          <Header navigation={navigation} isAdminPage />

          <ScrollView contentContainerStyle={styles.scrollContent}>
            <View style={styles.page}>
              {/* Cabeçalho da página */}
              <View style={styles.headerBlock}>
                <Text style={[styles.title, isMobile && { fontSize: 24 }]}>Tela de login</Text>
                <Text style={styles.subtitle}>
                  Personalize as cores, imagens e o botão da tela de login do seu app web e mobile.
                  A imagem lateral aparece somente na versão web (desktop).
                </Text>
              </View>

              {isLoadingColorInfo ? (
                <View style={styles.loadingBox}>
                  <ActivityIndicator color={ADMIN_COLORS.teal} size="large" />
                </View>
              ) : (
                <>
                  {/* Formulário (oculto no modo ampliado) */}
                  {!isLoginFullWidth && (
                    <View style={styles.formCol}>
                      <FormCustomLogin />

                      <View style={styles.saveRow}>
                        <Pressable
                          onPress={handleSave}
                          style={({ hovered }) => [
                            styles.primaryBtn,
                            isSave && { backgroundColor: ADMIN_COLORS.success },
                            hovered && !isSave && { backgroundColor: ADMIN_COLORS.accentDark },
                          ]}
                        >
                          <Text style={styles.primaryBtnText}>{isSave ? "Salvo!" : "Salvar"}</Text>
                        </Pressable>
                        <Text style={styles.autosaveHint}>
                          As alterações são salvas automaticamente.
                        </Text>
                      </View>
                    </View>
                  )}

                  {/* Pré-visualização ao vivo */}
                  <View style={styles.previewCard}>
                    <View style={styles.previewHeader}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.previewTitle}>Pré-visualização</Text>
                        <Text style={styles.previewSub}>Prévia em tempo real da tela de login.</Text>
                      </View>
                      <Pressable
                        onPress={() => setIsLoginFullWidth((v) => !v)}
                        style={({ hovered }) => [
                          styles.toggleBtn,
                          hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                        ]}
                      >
                        <Feather
                          name={isLoginFullWidth ? "minimize-2" : "maximize-2"}
                          size={15}
                          color={ADMIN_COLORS.ink}
                        />
                        <Text style={styles.toggleBtnText}>
                          {isLoginFullWidth ? "Reduzir" : "Ampliar"}
                        </Text>
                      </Pressable>
                    </View>

                    <View style={[styles.previewBody, { height: isLoginFullWidth ? "85vh" : 640 }]}>
                      <Login />
                    </View>
                  </View>
                </>
              )}
            </View>
          </ScrollView>
        </View>
      </ProviderPaper>
    </ViewportProvider>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    padding: 24,
    alignItems: "center",
  },
  page: {
    width: "100%",
    maxWidth: 900,
  },
  headerBlock: {
    marginBottom: 20,
  },
  title: {
    fontFamily: ADMIN_FONT,
    fontSize: 28,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
  },
  subtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    marginTop: 4,
  },
  loadingBox: {
    paddingVertical: 80,
    alignItems: "center",
    justifyContent: "center",
  },
  formCol: {
    width: "100%",
  },
  saveRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 4,
    marginBottom: 20,
  },
  primaryBtn: {
    height: 44,
    borderRadius: 10,
    paddingHorizontal: 24,
    backgroundColor: ADMIN_COLORS.teal,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  primaryBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 15,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  autosaveHint: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
  },
  previewCard: {
    width: "100%",
    backgroundColor: ADMIN_COLORS.surface,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: ADMIN_COLORS.radius,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  },
  previewHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.border,
  },
  previewTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 16,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
  },
  previewSub: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    marginTop: 2,
  },
  toggleBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surface,
    cursor: "pointer",
  },
  toggleBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: "600",
    color: ADMIN_COLORS.ink,
  },
  previewBody: {
    width: "100%",
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    overflow: "hidden",
  },
});
